import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useState } from 'react'
import { TwoFactorLoginStep } from '@/components/TwoFactorLoginStep'
import { useAuth } from '@/hooks/useAuth'
import type { ApiError } from '@/lib/api'
import styles from '../LoginPage/LoginPage.module.css'

function extractErrorMessage(error: unknown): string {
  const fallback = 'Não foi possível entrar. Verifique seus dados.'
  if (!error || typeof error !== 'object') return fallback
  const apiError = error as ApiError
  const details = apiError.details as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined

  if (details?.errors) {
    const firstKey = Object.keys(details.errors)[0]
    const firstMessage = firstKey ? details.errors[firstKey]?.[0] : undefined
    if (firstMessage) return firstMessage
  }

  if (details?.message) return details.message
  return apiError.message || fallback
}

export function AdminLoginPage() {
  const navigate = useNavigate()
  const { loginAdmin, completeAdminTwoFactorLogin } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function handlePasswordSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      const result = await loginAdmin({ email, password })
      if (result.status === 'two_factor_required') {
        setChallengeToken(result.challengeToken)
        setTotpCode('')
        return
      }
      navigate('/home-drftadmin')
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleTwoFactorSubmit() {
    if (!challengeToken) return
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      await completeAdminTwoFactorLogin({ challengeToken, code: totpCode })
      navigate('/home-drftadmin')
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>
          {challengeToken ? 'Verificação em duas etapas' : 'Entrar como Admin'}
        </h1>
        <p className={styles.subtitle}>
          {challengeToken
            ? 'Abra o Google Authenticator e informe o código de 6 dígitos.'
            : 'Acesso exclusivo para administradores'}
        </p>

        {challengeToken ? (
          <TwoFactorLoginStep
            code={totpCode}
            onCodeChange={setTotpCode}
            onSubmit={() => {
              void handleTwoFactorSubmit()
            }}
            onBack={() => {
              setChallengeToken(null)
              setTotpCode('')
              setErrorMessage(null)
            }}
            isSubmitting={isSubmitting}
            errorMessage={errorMessage}
          />
        ) : (
          <form className={styles.form} onSubmit={handlePasswordSubmit}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="admin-login-email">
                E-mail
              </label>
              <input
                id="admin-login-email"
                type="email"
                className={styles.input}
                placeholder="seu@email.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="admin-login-password">
                Senha
              </label>
              <input
                id="admin-login-password"
                type="password"
                className={styles.input}
                placeholder="********"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {errorMessage && (
              <div className={styles.field} role="alert">
                <span className={styles.subtitle}>{errorMessage}</span>
              </div>
            )}

            <button type="submit" className={styles.submit} disabled={isSubmitting}>
              <Icon icon="mdi:login" className={styles.submitIcon} />
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
